import { useQuery } from '@tanstack/react-query'
import type {
  IBuiltInInstrumentDetail,
  IBuiltInInstrumentListResponse,
} from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'

export const builtInInstrumentKeys = {
  all: ['built-in-instruments'] as const,
  list: () => [...builtInInstrumentKeys.all, 'list'] as const,
  detail: (id: string) => [...builtInInstrumentKeys.all, 'detail', id] as const,
}

export async function fetchBuiltInInstruments(
  signal?: AbortSignal,
): Promise<IBuiltInInstrumentListResponse> {
  const { data: envelope } = await apiClient.get<ApiResponse<IBuiltInInstrumentListResponse>>(
    '/api/v1/built-in-instruments',
    { signal },
  )
  if (!envelope?.data) {
    throw new Error('Built-in instruments response missing data')
  }
  return envelope.data
}

export async function fetchBuiltInInstrumentById(
  id: string,
  signal?: AbortSignal,
): Promise<IBuiltInInstrumentDetail> {
  const { data: envelope } = await apiClient.get<ApiResponse<IBuiltInInstrumentDetail>>(
    `/api/v1/built-in-instruments/${encodeURIComponent(id)}`,
    { signal },
  )
  if (!envelope?.data?.id) {
    throw new Error('Built-in instrument response missing data')
  }
  return envelope.data
}

export function useBuiltInInstruments() {
  return useQuery({
    queryKey: builtInInstrumentKeys.list(),
    queryFn: ({ signal }) => fetchBuiltInInstruments(signal),
  })
}

export function useBuiltInInstrumentDetail(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: builtInInstrumentKeys.detail(id ?? ''),
    queryFn: ({ signal }) => fetchBuiltInInstrumentById(id!, signal),
    enabled: enabled && Boolean(id),
  })
}
